import {useSearchParams} from 'react-router-dom';
import {useCallback, useMemo} from 'react';
import {SingleValue} from 'react-select';
import {formatKm} from '../../../support/format_distance';

interface MileageOption {
    label: string;
    value: number;
}
export function useMileages(mileageFrom: number | undefined, mileageTill: number | undefined) {
    const mileageOptions: MileageOption[] = useMemo(() => {
        const mileages = [0, 5000, 10000, 20000, 40000, 60000, 80000, 100000, 125000, 150000, 200000];

        return mileages.map((mileage) => {
            return {
                label: formatKm(mileage) ?? mileage.toString(),
                value: mileage,
            };
        });
    }, []);

    const mileageFromOptions = useMemo(() => {
        return mileageOptions.filter((mileage) => {
            if (mileageTill == null) {
                return true;
            }
            return mileageTill > mileage.value;
        });
    }, [mileageOptions, mileageTill]);
    const mileageTillOptions = useMemo(() => {
        return mileageOptions.filter((mileage) => {
            if (mileageFrom == null) {
                return true;
            }
            return mileageFrom < mileage.value;
        });
    }, [mileageOptions, mileageFrom]);

    const selectedMileageFromOption = useMemo(() => {
        return mileageFrom != null ? mileageFromOptions.find((option) => option.value === mileageFrom) : null;
    }, [mileageFrom, mileageFromOptions]);
    const selectedMileageTillOption = useMemo(() => {
        return mileageTill != null ? mileageTillOptions.find((option) => option.value === mileageTill) : null;
    }, [mileageTill, mileageTillOptions]);

    const [, setSearchParams] = useSearchParams();
    const onSetMileageFromCallback = useCallback(
        (value: SingleValue<MileageOption>) => {
            setSearchParams((current) => {
                if (value?.value) {
                    current.set('mileage_from', value.value.toString());
                } else {
                    current.delete('mileage_from');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );
    const onSetMileageTillCallback = useCallback(
        (value: SingleValue<MileageOption>) => {
            setSearchParams((current) => {
                if (value?.value) {
                    current.set('mileage_till', value.value.toString());
                } else {
                    current.delete('mileage_till');
                }
                current.delete('page');
                return current;
            });
        },
        [setSearchParams],
    );

    return {
        mileageFromOptions,
        mileageTillOptions,
        selectedMileageFromOption,
        selectedMileageTillOption,
        onSetMileageFromCallback,
        onSetMileageTillCallback,
    };
}
